import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { KeywordTagInput } from "@/components/admin/KeywordTagInput";
import { Badge } from "@/components/ui/badge";
import { Save, RefreshCw, Target, Check, AlertTriangle, Globe, Search } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";

// The static pages the admin can manage SEO for.
const PAGES = [
  { key: "home", label: "Home", path: "/" },
  { key: "about", label: "About", path: "/about" },
  { key: "services", label: "Psychedelic-Assisted Psychotherapy", path: "/services" },
  { key: "fees", label: "Psychotherapy Intensive Options", path: "/fees" },
  { key: "book", label: "Worry Less & Beat That Anxiety Book", path: "/book" },
  { key: "blog", label: "Blog (index)", path: "/blog" },
  { key: "contact", label: "Contact", path: "/contact" },
  {
    key: "miami-specialty",
    label: "Ketamine Therapy for Hyper-Independent Women in Brickell Miami, FL",
    path: "/specialties/online-ketamine-therapy-women-miami",
  },
  {
    key: "cumberland-specialty",
    label: "Ketamine Therapy for Hyper-Independent Women in Cumberland, ME",
    path: "/specialties/ketamine-therapy-hyper-independent-women-cumberland-maine",
  },
  {
    key: "portland-specialty",
    label: "Ketamine Therapy for Hyper-Independent Women in Portland, ME",
    path: "/specialties/ketamine-therapy-hyper-independent-women-portland-maine",
  },
  {
    key: "washington-dc-specialty",
    label: "Ketamine Therapy for Hyper-Independent Women in Washington, DC",
    path: "/specialties/ketamine-therapy-hyper-independent-women-washington-dc",
  },
  {
    key: "group-immersion",
    label: "9-Week Group Immersion Intensive (FL & ME)",
    path: "/specialties/ketamine-therapy-group-immersion-fl-me",
  },
  { key: "faq-page", label: "FAQ Page", path: "/faq" },
  {
    key: "hyper-independence-specialty",
    label: "Therapy for Hyper-Independence",
    path: "/specialties/therapy-for-hyper-independence",
  },
  {
    key: "boundaries-therapist-specialty",
    label: "Boundaries Therapist (Emotionally Immature Parents)",
    path: "/specialties/boundaries-therapist-emotionally-immature-parents",
  },
  {
    key: "disorganized-attachment-specialty",
    label: "Disorganized Attachment & KAP",
    path: "/specialties/disorganized-attachment-kap",
  },
];

const has = (v) => !!(v && v.trim());
const incl = (v, kw) => has(v) && has(kw) && v.toLowerCase().includes(kw.trim().toLowerCase());

const computeSeoScore = (d = {}) => {
  const { focus_keyword: kw, meta_title: t, meta_description: m, image_alt: a } = d;
  const tl = (t || "").trim().length;
  const ml = (m || "").trim().length;
  let score = 0;
  if (has(kw)) score += 20;
  if (has(t)) score += tl >= 30 && tl <= 60 ? 15 : 8;
  if (incl(t, kw)) score += 15;
  if (has(m)) score += ml >= 120 && ml <= 160 ? 20 : 10;
  if (incl(m, kw)) score += 15;
  if (has(a)) score += 8;
  if (incl(a, kw)) score += 7;
  return score;
};

const scoreTone = (score) =>
  score >= 80
    ? { cls: "bg-green-100 text-green-800 border-green-200", label: "Strong" }
    : score >= 50
      ? { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Needs work" }
      : { cls: "bg-red-100 text-red-700 border-red-200", label: "Weak" };

const ScoreBadge = ({ score, testId, showLabel = false }) => {
  const tone = scoreTone(score);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold tabular-nums ${tone.cls}`}
      title={`SEO score ${score}/100 — ${tone.label}`}
      data-testid={testId}
    >
      {score}
      {showLabel && <span className="font-medium">· {tone.label}</span>}
    </span>
  );
};

const emptyForm = { focus_keyword: "", secondary_keywords: [], meta_title: "", meta_description: "", image_alt: "" };

const CheckPill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
      ok ? "bg-green-50 text-green-800 border border-green-200" : "bg-amber-50 text-amber-800 border border-amber-200"
    }`}
  >
    {ok ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
    {label}
  </span>
);

const PageSeoManager = ({ adminPassword }) => {
  const [activeKey, setActiveKey] = useState(PAGES[0].key);
  const [form, setForm] = useState(emptyForm);
  const [allOverrides, setAllOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const activePage = PAGES.find((p) => p.key === activeKey);

  const fetchAll = useCallback(async () => {
    if (!adminPassword) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/page-seo`, {
        params: { password: adminPassword },
      });
      const map = {};
      data.forEach((d) => {
        map[d.page_key] = d;
      });
      setAllOverrides(map);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load overrides";
      toast.error(typeof msg === "string" ? msg : "Failed to load overrides");
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // When the user switches pages or overrides finish loading, repopulate the form
  useEffect(() => {
    const existing = allOverrides[activeKey];
    if (existing) {
      setForm({
        focus_keyword: existing.focus_keyword || "",
        secondary_keywords: existing.secondary_keywords || [],
        meta_title: existing.meta_title || "",
        meta_description: existing.meta_description || "",
        image_alt: existing.image_alt || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [activeKey, allOverrides]);

  const handleField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        focus_keyword: form.focus_keyword.trim() || null,
        secondary_keywords: form.secondary_keywords,
        meta_title: form.meta_title.trim() || null,
        meta_description: form.meta_description.trim() || null,
        image_alt: form.image_alt.trim() || null,
      };
      const { data } = await axios.put(
        `${API}/admin/page-seo/${activeKey}`,
        payload,
        { params: { password: adminPassword } }
      );
      setAllOverrides((prev) => ({ ...prev, [activeKey]: data }));
      toast.success(`Saved SEO overrides for ${activePage?.label}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save";
      toast.error(typeof msg === "string" ? msg : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const kw = form.focus_keyword.trim();
  const metaLen = form.meta_description.length;
  const titleLen = form.meta_title.trim().length;
  const titleLenOk = titleLen >= 30 && titleLen <= 60;
  const kwInTitle = kw && form.meta_title.toLowerCase().includes(kw.toLowerCase());
  const defaultTitle = `${activePage?.label || "Page"} | Divine Love Wellness Enterprises`;
  const altLen = form.image_alt.length;
  const kwLower = kw.toLowerCase();
  const kwInMeta = kw && form.meta_description.toLowerCase().includes(kwLower);
  const kwInAlt = kw && form.image_alt.toLowerCase().includes(kwLower);
  const metaLenOk = metaLen >= 120 && metaLen <= 160;
  const altOk = altLen > 0 && altLen <= 160;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="page-seo-manager"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h2 className="font-serif text-xl font-semibold text-foreground">
            Pages SEO
          </h2>
          <p className="text-sm text-muted-foreground">
            Override focus keyword, secondary keywords, meta title, meta description and image alt text for each page.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          disabled={loading}
          className="rounded-full"
          data-testid="page-seo-refresh"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Page picker */}
        <div className="space-y-1.5" data-testid="page-seo-picker">
          <p className="text-xs text-muted-foreground px-1 pb-1 leading-snug" data-testid="page-seo-score-legend">
            SEO score (0–100) from saved overrides:{" "}
            <span className="text-green-700 font-medium">80+ strong</span>,{" "}
            <span className="text-amber-700 font-medium">50–79 needs work</span>,{" "}
            <span className="text-red-700 font-medium">&lt;50 weak</span>.
          </p>
          {PAGES.map((p) => {
            const isActive = activeKey === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setActiveKey(p.key)}
                data-testid={`page-seo-tab-${p.key}`}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-colors flex items-start justify-between gap-2 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-white hover:bg-secondary/50 text-foreground"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium leading-snug">{p.label}</div>
                  <div className={`text-xs mt-0.5 truncate ${isActive ? "opacity-80" : "text-muted-foreground"}`}>
                    {p.path}
                  </div>
                </div>
                <span className="mt-0.5 flex-shrink-0">
                  <ScoreBadge score={computeSeoScore(allOverrides[p.key])} testId={`page-seo-score-${p.key}`} />
                </span>
              </button>
            );
          })}
        </div>

        {/* Editor */}
        <Card className="bg-white rounded-2xl p-6 space-y-5" data-testid="page-seo-editor">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Globe className="h-4 w-4" />
            Editing: <span className="font-medium text-foreground">{activePage?.label}</span>
            <span className="ml-auto flex items-center gap-2">
              <ScoreBadge score={computeSeoScore(form)} showLabel testId="page-seo-live-score" />
              <Badge variant="outline" className="rounded-full text-xs">
                {activePage?.path}
              </Badge>
            </span>
          </div>

          <div className="space-y-2">
            <Label htmlFor="psm-keyword" className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              Focus Keyword
            </Label>
            <Input
              id="psm-keyword"
              value={form.focus_keyword}
              onChange={handleField("focus_keyword")}
              placeholder="e.g. online ketamine therapy Miami women"
              maxLength={160}
              className="rounded-xl"
              data-testid="page-seo-keyword-input"
            />
            <p className="text-xs text-muted-foreground">
              The main phrase you want this page to rank for on Google.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="psm-secondary">Secondary Keywords</Label>
            <KeywordTagInput
              id="psm-secondary"
              value={form.secondary_keywords}
              onChange={(next) => setForm((prev) => ({ ...prev, secondary_keywords: next }))}
              placeholder="e.g. ketamine therapy, trauma recovery, virtual therapy"
              testId="page-seo-secondary-keywords"
            />
            <p className="text-xs text-muted-foreground">
              Press Enter or type a comma to add each one (up to 20). Added to the page&rsquo;s meta keywords tag and structured data.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="psm-title">Meta Title (SEO Title)</Label>
            <Input
              id="psm-title"
              value={form.meta_title}
              onChange={handleField("meta_title")}
              placeholder={defaultTitle}
              maxLength={120}
              className="rounded-xl"
              data-testid="page-seo-title-input"
            />
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className={`text-muted-foreground ${titleLenOk ? "text-green-700" : ""}`} data-testid="page-seo-title-count">
                {titleLen} / 60 chars
              </span>
              {kw && titleLen > 0 && <CheckPill ok={kwInTitle} label={kwInTitle ? "Includes keyword" : "Missing keyword"} />}
              {titleLen > 0 && <CheckPill ok={titleLenOk} label={titleLenOk ? "Good length" : "Aim for 30–60"} />}
            </div>
            <p className="text-xs text-muted-foreground">
              Used exactly as the browser tab &amp; Google headline. Leave blank to use the default title.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="psm-meta">Meta Description</Label>
            <Textarea
              id="psm-meta"
              value={form.meta_description}
              onChange={handleField("meta_description")}
              placeholder="A compelling 1-2 sentence summary that will appear in Google results. Aim for 120-160 characters."
              rows={3}
              maxLength={200}
              className="rounded-xl resize-none"
              data-testid="page-seo-meta-input"
            />
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className={`text-muted-foreground ${metaLenOk ? "text-green-700" : ""}`}>
                {metaLen} / 160 chars
              </span>
              {kw && <CheckPill ok={kwInMeta} label={kwInMeta ? "Includes keyword" : "Missing keyword"} />}
              {metaLen > 0 && <CheckPill ok={metaLenOk} label={metaLenOk ? "Good length" : "Aim for 120–160"} />}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="psm-alt">Image Alt Text</Label>
            <Input
              id="psm-alt"
              value={form.image_alt}
              onChange={handleField("image_alt")}
              placeholder="Describe the page's main image for screen readers and Google Images."
              maxLength={200}
              className="rounded-xl"
              data-testid="page-seo-alt-input"
            />
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className={`text-muted-foreground ${altOk ? "text-green-700" : ""}`}>
                {altLen} / 160 chars
              </span>
              {kw && altLen > 0 && (
                <CheckPill ok={kwInAlt} label={kwInAlt ? "Includes keyword" : "Keyword not in alt"} />
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="page-seo-save"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save SEO Overrides"}
            </Button>
          </div>

          {/* Google search-result preview */}
          <div className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Google Preview
              </p>
            </div>
            <div
              className="rounded-xl bg-white border border-border p-4"
              data-testid="page-seo-google-preview"
            >
              <p className="text-xs text-[#202124] truncate">
                divinelovewellness.net
                {activePage?.path && activePage.path !== "/" ? ` › ${activePage.path.replace(/^\//, "").replace(/\//g, " › ")}` : ""}
              </p>
              <p className="text-[#1a0dab] text-lg leading-snug mt-0.5 hover:underline cursor-default">
                {form.meta_title.trim() || defaultTitle}
              </p>
              <p className="text-sm text-[#4d5156] mt-1 leading-snug">
                {form.meta_description.trim().length > 0
                  ? (form.meta_description.length > 160
                    ? form.meta_description.slice(0, 157) + "..."
                    : form.meta_description)
                  : "Your meta description will appear here. Enter one above for the best result in Google."}
              </p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground pt-2 border-t border-border/40">
            <span className="font-semibold">Note:</span> all site pages are wired
            up to read these overrides. Leave a field empty to use the page&rsquo;s
            hardcoded default.
          </p>
        </Card>
      </div>
    </motion.div>
  );
};

export default PageSeoManager;
